/**
 * userPrefsStore.js — ELYON-SHELL-FOUNDATION-1
 *
 * Zustand store for user preferences: theme, density, sidebar state, locale.
 * Persisted to localStorage.
 *
 * Ported from elyon-app/src/stores/userPrefsStore.ts — adapted to plain JS
 * with Zustand v5 pattern.
 *
 * Primitive selectors prevent object-identity re-renders per Zustand best practice.
 */
import { create } from 'zustand';
import { persist } from 'zustand/middleware';

const STORAGE_PREFIX = 'elyon_prefs';

const DEFAULTS = {
  theme:             'platform-light',
  density:           'standard',
  locale:            'en-US',
  sidebarCollapsed:  false,
  sidebarHidden:     false,
  sidebarWidth:      264,
  starredModules:    [],
  notificationStyle: 'count',
  aegisPanelOpen:    false,
  commandPaletteOpen: false,
  // Transient: query staged by a surface (e.g. command center) to pre-populate
  // the AEGIS panel composer on open. Never persisted.
  aegisDraftQuery:   '',
};

export const useUserPrefsStore = create(
  persist(
    (set, get) => ({
      ...DEFAULTS,

      setTheme: (theme) => {
        set({ theme });
        document.documentElement.setAttribute('data-theme', theme.split('-')[0]);
        document.documentElement.setAttribute('data-mode',  theme.split('-')[1] ?? 'light');
      },

      setDensity: (density) => {
        set({ density });
        document.documentElement.setAttribute('data-density', density);
      },

      setLocale: (locale) => {
        set({ locale });
        document.documentElement.setAttribute('lang', locale);
        const rtl = ['ar-SA', 'he-IL'].includes(locale);
        document.documentElement.setAttribute('dir', rtl ? 'rtl' : 'ltr');
      },

      setSidebarCollapsed:  (sidebarCollapsed)  => set({ sidebarCollapsed }),
      setSidebarHidden:     (sidebarHidden)     => set({ sidebarHidden }),
      setSidebarWidth:      (sidebarWidth)      => set({ sidebarWidth }),
      setNotificationStyle: (notificationStyle) => set({ notificationStyle }),

      setAegisPanelOpen:     (aegisPanelOpen)     => set({ aegisPanelOpen }),
      setCommandPaletteOpen: (commandPaletteOpen) => set({ commandPaletteOpen }),
      setAegisDraftQuery:    (aegisDraftQuery)    => set({ aegisDraftQuery }),

      toggleStar: (moduleId) =>
        set((s) => ({
          starredModules: s.starredModules.includes(moduleId)
            ? s.starredModules.filter((id) => id !== moduleId)
            : [...s.starredModules, moduleId],
        })),

      applyToDocument: () => {
        const { theme, density, locale } = get();
        const [palette, mode] = theme.split('-');
        document.documentElement.setAttribute('data-theme',   palette);
        document.documentElement.setAttribute('data-mode',    mode ?? 'light');
        document.documentElement.setAttribute('data-density', density);
        document.documentElement.setAttribute('lang', locale);
        const rtl = ['ar-SA', 'he-IL'].includes(locale);
        document.documentElement.setAttribute('dir', rtl ? 'rtl' : 'ltr');
      },

      resetForTenant: ({ defaultTheme, defaultDensity, defaultLocale }) => {
        set({ theme: defaultTheme, density: defaultDensity, locale: defaultLocale });
      },
    }),
    {
      name: `${STORAGE_PREFIX}_global`,
      partialize: (s) => ({
        theme:             s.theme,
        density:           s.density,
        locale:            s.locale,
        sidebarCollapsed:  s.sidebarCollapsed,
        sidebarHidden:     s.sidebarHidden,
        sidebarWidth:      s.sidebarWidth,
        starredModules:    s.starredModules,
        notificationStyle: s.notificationStyle,
      }),
    }
  )
);

/* Primitive selectors — avoids object-spread closures that cause infinite renders. */
export const useTheme             = () => useUserPrefsStore((s) => s.theme);
export const useDensity           = () => useUserPrefsStore((s) => s.density);
export const useLocale            = () => useUserPrefsStore((s) => s.locale);
export const useSidebarCollapsed  = () => useUserPrefsStore((s) => s.sidebarCollapsed);
export const useSidebarHidden     = () => useUserPrefsStore((s) => s.sidebarHidden);
export const useSidebarWidth      = () => useUserPrefsStore((s) => s.sidebarWidth);
export const useStarredModules    = () => useUserPrefsStore((s) => s.starredModules);
export const useNotificationStyle = () => useUserPrefsStore((s) => s.notificationStyle);
export const useAegisPanelOpen    = () => useUserPrefsStore((s) => s.aegisPanelOpen);
export const useCommandPaletteOpen = () => useUserPrefsStore((s) => s.commandPaletteOpen);
export const useAegisDraftQuery   = () => useUserPrefsStore((s) => s.aegisDraftQuery);
